import { differenceInCalendarDays, differenceInMinutes, formatDistanceToNowStrict, isSameDay, isToday, isTomorrow, isYesterday } from "date-fns";
import { EMPTY } from "@/lib/formatters";

const time = new Intl.DateTimeFormat("en-US", { hour: "numeric", minute: "2-digit" });
const dayShort = new Intl.DateTimeFormat("en-US", { weekday: "short", month: "short", day: "numeric" });
const dateShort = new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric" });
const dateFull = new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric", year: "numeric" });

export function toDate(value: string | Date | null | undefined): Date | null {
  if (!value) return null;
  const d = value instanceof Date ? value : new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** "3m ago", "2h ago", "4d ago" */
export function formatRelative(value: string | Date | null | undefined, now: Date = new Date()): string {
  const d = toDate(value);
  if (!d) return EMPTY;
  if (Math.abs(differenceInMinutes(now, d)) < 1) return "just now";
  const s = formatDistanceToNowStrict(d, { addSuffix: false })
    .replace(/ seconds?/, "s")
    .replace(/ minutes?/, "m")
    .replace(/ hours?/, "h")
    .replace(/ days?/, "d")
    .replace(/ months?/, "mo")
    .replace(/ years?/, "y");
  return d > now ? `in ${s}` : `${s} ago`;
}

export function formatTime(value: string | Date | null | undefined): string {
  const d = toDate(value);
  return d ? time.format(d) : EMPTY;
}

/** "Today 2:30 PM", "Tomorrow 10:00 AM", "Thu, Oct 2 · 9:00 AM" */
export function formatWhen(value: string | Date | null | undefined): string {
  const d = toDate(value);
  if (!d) return EMPTY;
  if (isToday(d)) return `Today ${time.format(d)}`;
  if (isTomorrow(d)) return `Tomorrow ${time.format(d)}`;
  if (isYesterday(d)) return `Yesterday ${time.format(d)}`;
  return `${dayShort.format(d)} · ${time.format(d)}`;
}

export function formatDate(value: string | Date | null | undefined, opts?: { year?: boolean }): string {
  const d = toDate(value);
  if (!d) return EMPTY;
  return (opts?.year ? dateFull : dateShort).format(d);
}

/** Label for a day separator in a transcript. */
export function dayLabel(value: string | Date): string {
  const d = toDate(value) ?? new Date();
  if (isToday(d)) return "Today";
  if (isYesterday(d)) return "Yesterday";
  return dayShort.format(d);
}

export function sameDay(a: string, b: string): boolean {
  const da = toDate(a);
  const db = toDate(b);
  return Boolean(da && db && isSameDay(da, db));
}

export function daysSince(value: string | null | undefined, now: Date = new Date()): number | null {
  const d = toDate(value);
  return d ? differenceInCalendarDays(now, d) : null;
}

export function minutesBetween(a: string, b: string): number {
  const da = toDate(a);
  const db = toDate(b);
  return da && db ? Math.abs(differenceInMinutes(db, da)) : Number.POSITIVE_INFINITY;
}

/** Value for <input type="datetime-local"> in the browser's timezone. */
export function toLocalInputValue(value: string | Date): string {
  const d = toDate(value) ?? new Date();
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
